















import { computed, defineComponent, PropType, ref } from '@nuxtjs/composition-api';
import useCartCookie from '@shared/composables/useCartCookie';
import useFeatureFlag from '@shared/composables/useFeatureFlag';
import useFitment from '@shared/composables/useFitment';
import useFitmentData from '@shared/composables/useFitmentData';
import useLocalStorageCart from '@shared/composables/useLocalStorageCart';
import useMyGarage from '@shared/composables/useMyGarage';
import useSearchInsights from '@shared/composables/useSearchInsights';
import useUiNotification from '@shared/composables/useUiNotification';
import { getProductFailedToAddCartNotification } from '@shared/composables/useUiNotification/commonNotifications';
import useUiState from '@shared/composables/useUiState';
import useVehicleSelectionSparesbox from '@shared/composables/useVehicleSelectionSparesbox';
import analytics, { ShopifyProduct } from '@shared/helpers/analytics';
import { decodeBase64 } from '@shared/helpers/decodeBase64';
import { getFitmentStatus } from '@shared/helpers/fitment/getFitmentStatus';
import { searchGetters } from '@unified-commerce/gpc-vue-storefront-search';
import { useUser } from '@unified-commerce/gpc-vue-storefront-shopify';

import ProductCardFitment from './ProductCardFitment.vue';
import ProductCardView from './ProductCardView.vue';
import { IProductCardProduct, IProductCardSize } from './utils/types';

export default defineComponent({
  name: 'ProductCard',
  components: {
    ProductCardView,
    ProductCardFitment,
  },
  props: {
    product: {
      type: Object as PropType<IProductCardProduct>,
      required: true,
    },
    size: {
      type: String as PropType<IProductCardSize>,
      default: 'standard',
    },
    showActionButton: {
      type: Boolean,
      default: false,
    },
  },
  // eslint-disable-next-line max-lines-per-function
  setup({ product }) {
    const { user } = useUser();
    const { selectedVehicle: selectedVehicleAMX } = useMyGarage();
    const { userSelectedVehicle: userSelectedVehicleSparesbox } = useVehicleSelectionSparesbox();
    const { send: sendUiNotification } = useUiNotification();
    const { addItem, error } = useCartCookie();
    const { setRecentlyAddedProduct } = useUiState();
    const { setCartLocalStorage } = useLocalStorageCart();
    const { productsFitmentResult } = useFitmentData();
    const { isFitmentRoute, showFittingParts } = useFitment();
    const { isFeatureEnabled } = useFeatureFlag();
    const { eventProductClicked, eventProductAddedToCart } = useSearchInsights();

    const userSelectedVehicle = computed(
      () => userSelectedVehicleSparesbox.value ?? selectedVehicleAMX.value,
    );
    const fitmentData = computed(() => searchGetters.getItems(productsFitmentResult.value));

    const addToCartLoading = ref(false);

    const showFitment = computed(
      () =>
        isFitmentRoute.value &&
        showFittingParts &&
        !!userSelectedVehicle.value &&
        isFeatureEnabled('fitmentPhase1'),
    );

    const productDetail = {
      id: product.id,
      name: product.name,
      vendor: product.brand,
      variantId: product.variantId as string,
      merchandiseId: product.merchandiseId as string,
    };

    const fittingPositions = computed(() => {
      if (!Array.isArray(fitmentData.value)) {
        return '';
      }
      const fitmentEntry = fitmentData.value.find(
        (fitmentEntry) => product.sku && fitmentEntry?.record?.shopifysku?.includes(product.sku),
      );

      // .split('::') because data is in format "engine bay::1" for some reason
      const criteria: string[] = fitmentEntry?.record?.criteria_values?.split(';;') ?? [];
      if (!criteria.length) return '';

      const splitCriteria =
        criteria
          .map((criteria) => criteria.split('::'))
          .find((criteria) => criteria.includes('Fitting Position')) ?? [];

      if (splitCriteria.length) {
        return splitCriteria[1] || '';
      }
      return '';
    });

    const fitmentStatus = computed(() => {
      return getFitmentStatus({
        fitsVehicle: product.fitsVehicle,
        hasFitment: product.hasFitment,
        isUniversal: product.isUniversalFitment,
      });
    });

    const selectItemAnalytics = () => {
      const objectId = product.objectId;
      const position = product.position;
      const queryId = product.queryId;

      analytics({
        event: 'select_item',
        product: productDetail as ShopifyProduct,
        discount: product.compareToPrice,
        price: product.price,
        customer_id: user?.value?.id,
        objectId,
        position,
        queryId,
      });
      if (objectId && position && queryId) {
        eventProductClicked({
          objectId,
          position,
          queryId,
        });
      }
    };

    const addToCart = async () => {
      try {
        addToCartLoading.value = true;
        const quantity = 1;
        await addItem({ product: productDetail, quantity, queryId: product.queryId });

        if (product.objectId) {
          eventProductAddedToCart({
            source: 'PLP',
            queryId: product.queryId,
            quantity,
            objectId: product.objectId,
            price: product.price,
            compareToPrice: product.compareToPrice,
          });
        }

        analytics({
          event: 'add_to_cart',
          id: product.id,
          name: product.name,
          price: product.price,
          vendor: product.brand,
          customer_id: user?.value?.id,
          objectId: product.objectId,
        });

        setCartLocalStorage();

        if (error.value.addItem) {
          sendUiNotification(
            getProductFailedToAddCartNotification({ name: product.name as string }),
          );
        } else {
          setRecentlyAddedProduct({
            name: product.name as string,
            brand: product.brand as string,
            price: (product.compareToPrice as number) || (product.price as number),
            specialPrice: product.compareToPrice ? product.price : null,
            sku: product.sku as string,
            partNumber: String(product.partNumber),
            img: product.productImage,
            variantId: decodeBase64(product.variantId as string),
          });
        }
      } catch (err) {
        sendUiNotification(getProductFailedToAddCartNotification({ name: product.name as string }));
      } finally {
        addToCartLoading.value = false;
      }
    };

    return {
      addToCartLoading,
      showFitment,
      fitmentStatus,
      fittingPositions,
      selectItemAnalytics,
      addToCart,
    };
  },
});
